import { useEffect, useMemo, useRef, useState } from "react";
import { Camera, ImagePlus, Languages, Mail, MessageSquare, MoreHorizontal, Paperclip, Pause, PhoneCall, Play, SquarePlus, Volume2, VolumeX } from "lucide-react";
import { getMobilePlatform } from "@/utils/device";
import { useAddToHomeScreen } from "@/hooks/useAddToHomeScreen";
import { AddToHomeScreenSheet } from "./AddToHomeScreenSheet";
import { LanguagePopover } from "./LanguagePopover";
import { MagicalIconButton } from "./MagicalIconButton";
import { cn } from "@/lib/utils";

import type { MoreActionsPopoverProps } from "@/types/voice/more-actions";

/**
 * The row's overflow drawer for secondary, occasional actions — currently
 * "add a photo" and "pause the conversation". Neither is a caller's primary
 * need (start/stop, mute, switch to text), so both live one tap behind this
 * "More" trigger instead of taking a permanent slot in the row.
 *
 * Same shape as its neighbours (LanguagePopover / InfoPopover): own trigger,
 * own outside-click + Escape handling, no portal. Replaces the row's former
 * standalone add-photo trigger — the phone library/camera choice is now
 * flattened straight into this menu instead of a nested popover.
 */
export const MoreActionsPopover = ({
  brandColor,
  secondaryColor,
  size = 36,
  onAddImage,
  onTogglePause,
  isPaused = false,
  onOpenLanguages,
  onTalkToHuman,
  actionButtonStyle,
  showLanguage = false,
  onCallGenie,
  onEmailGenie,
  emailActionLabel = "Email the team",
  onEmailSummary,
  emailSummaryLabel = "Email summary",
  placement = "top",
  hideLabel = false,
  onToggleAgentVoice,
  isAgentVoiceMuted = false,
  onOpenChat,
  onOpenResources,
  addToHomeScreen,
}: MoreActionsPopoverProps) => {
  const [open, setOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const onPhone = useMemo(() => getMobilePlatform() !== "other", []);
  const showAddImage = typeof onAddImage === "function";
  const showPause = typeof onTogglePause === "function";
  const showLanguages = typeof onOpenLanguages === "function";
  const showTalkToHuman = typeof onTalkToHuman === "function";
  const homeScreen = useAddToHomeScreen();
  const showHomeScreen = !!addToHomeScreen && homeScreen.isVisible;

  useEffect(() => {
    if (!open) return;
    ref.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
    const onDocClick = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
        ref.current?.querySelector<HTMLButtonElement>('button[aria-label="More actions"]')?.focus();
      }
    };
    document.addEventListener("mousedown", onDocClick);
    // Handle Escape before the parent drawer's document-level listener.
    window.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  const choose = (action: () => void) => {
    setOpen(false);
    action();
  };

  if (!showAddImage && !showPause && !showLanguages && !showLanguage && !showTalkToHuman && !onCallGenie && !onEmailGenie && !onEmailSummary && !onToggleAgentVoice && !onOpenChat && !onOpenResources && !showHomeScreen) return null;

  return (
    <div ref={ref} className="relative inline-flex flex-col items-center">
      {/* Panel-only — its own trigger button is hidden; this menu's "Language"
          item opens it directly via controlled state. */}
      {showLanguage && (
        <LanguagePopover
          brandColor={brandColor}
          variant="modal"
          hideTrigger
          open={languageOpen}
          onOpenChange={setLanguageOpen}
        />
      )}
      <MagicalIconButton
        onClick={() => setOpen((v) => !v)}
        brandColor={brandColor}
        secondaryColor={secondaryColor}
        size={Math.max(size, 44)}
        ariaLabel="More actions"
        label={hideLabel ? undefined : "More"}
        activeState={open ? "active" : null}
        hoverHint="More actions"
        hintPlacement="top"
        idleBackgroundColor={actionButtonStyle?.idleBackgroundColor}
        activeBackgroundColor={actionButtonStyle?.activeBackgroundColor}
        iconColorOverride={actionButtonStyle?.iconColor}
      >
        <MoreHorizontal size={Math.round(16 * (size / 36))} strokeWidth={2} />
      </MagicalIconButton>

      {open && (
        <div
          role="menu"
          aria-label="More conversation actions"
          onKeyDown={(event) => {
            if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
            const items = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitem"]'));
            if (!items.length) return;
            event.preventDefault();
            const current = items.indexOf(document.activeElement as HTMLButtonElement);
            const next = event.key === "Home" ? 0 : event.key === "End" ? items.length - 1 :
              (current + (event.key === "ArrowDown" ? 1 : -1) + items.length) % items.length;
            items[next]?.focus();
          }}
          // "bottom" is the open chat drawer's header, where More sits at the
          // left edge — a right-anchored menu would run off the screen there.
          className={cn("absolute z-[1600] w-52 overflow-hidden rounded-2xl border border-genie-border bg-white py-1 shadow-lg [&_button]:min-h-[44px]", placement === "top" ? "right-0 bottom-full mb-3" : "left-0 top-full mt-3")}
        >
          {onToggleAgentVoice && (
            <button type="button" role="menuitem" onClick={() => choose(onToggleAgentVoice)}
              className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-sm font-semibold text-gray-700 hover:bg-gray-50">
              {isAgentVoiceMuted ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
              {isAgentVoiceMuted ? "Unmute Genie" : "Mute Genie"}
            </button>
          )}
          {onOpenChat && (
            <button type="button" role="menuitem" onClick={() => choose(onOpenChat)}
              className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-sm font-semibold text-gray-700 hover:bg-gray-50">
              <MessageSquare className="h-4 w-4" /> Show chat
            </button>
          )}
          {onOpenResources && (
            <button type="button" role="menuitem" onClick={() => choose(onOpenResources)}
              className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-sm font-semibold text-gray-700 hover:bg-gray-50">
              <Paperclip className="h-4 w-4" /> Resources
            </button>
          )}
          {showPause && (
            <button
              type="button"
              role="menuitem"
              onClick={() => choose(() => onTogglePause!())}
              className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-[13px] font-semibold text-[#3D4557] transition-colors hover:bg-[#F5F7FB]"
            >
              {isPaused ? (
                <Play size={16} strokeWidth={2} style={{ color: actionButtonStyle?.iconColor || brandColor }} />
              ) : (
                <Pause size={16} strokeWidth={2} style={{ color: actionButtonStyle?.iconColor || brandColor }} />
              )}
              {isPaused ? "Resume conversation" : "Pause conversation"}
            </button>
          )}
          {showAddImage && onPhone && (
            <>
              <button
                type="button"
                role="menuitem"
                onClick={() => choose(() => onAddImage!("library"))}
                className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-[13px] font-semibold text-[#3D4557] transition-colors hover:bg-[#F5F7FB]"
              >
                <ImagePlus size={16} strokeWidth={2} style={{ color: actionButtonStyle?.iconColor || brandColor }} />
                Photo library
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => choose(() => onAddImage!("camera"))}
                className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-[13px] font-semibold text-[#3D4557] transition-colors hover:bg-[#F5F7FB]"
              >
                <Camera size={16} strokeWidth={2} style={{ color: actionButtonStyle?.iconColor || brandColor }} />
                Take a photo
              </button>
            </>
          )}
          {showAddImage && !onPhone && (
            <button
              type="button"
              role="menuitem"
              onClick={() => choose(() => onAddImage!("library"))}
              className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-[13px] font-semibold text-[#3D4557] transition-colors hover:bg-[#F5F7FB]"
            >
              <ImagePlus size={16} strokeWidth={2} style={{ color: actionButtonStyle?.iconColor || brandColor }} />
              Add a photo
            </button>
          )}
          {showLanguage && (
            <button
              type="button"
              role="menuitem"
              onClick={() => choose(() => setLanguageOpen(true))}
              className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-[13px] font-semibold text-[#3D4557] transition-colors hover:bg-[#F5F7FB]"
            >
              <Languages size={16} strokeWidth={2} style={{ color: actionButtonStyle?.iconColor || brandColor }} />
              Talk in any language
            </button>
          )}
          {showLanguages && (
            <button
              type="button"
              role="menuitem"
              onClick={() => choose(() => onOpenLanguages!())}
              className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-[13px] font-semibold text-[#3D4557] transition-colors hover:bg-[#F5F7FB]"
            >
              <Languages size={16} strokeWidth={2} style={{ color: actionButtonStyle?.iconColor || brandColor }} />
              Languages
            </button>
          )}
          {showTalkToHuman && (
            <button
              type="button"
              role="menuitem"
              onClick={() => choose(() => onTalkToHuman!())}
              className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-[13px] font-semibold text-[#3D4557] transition-colors hover:bg-[#F5F7FB]"
            >
              <PhoneCall size={16} strokeWidth={2} style={{ color: actionButtonStyle?.iconColor || brandColor }} />
              Talk to human
            </button>
          )}
          {onCallGenie && (
            <button type="button" role="menuitem" onClick={() => choose(onCallGenie)}
              className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-sm font-semibold text-gray-700 hover:bg-gray-50">
              <PhoneCall className="h-4 w-4" /> Call the Genie
            </button>
          )}
          {onEmailSummary && (
            <button type="button" role="menuitem" onClick={() => choose(onEmailSummary)}
              className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-sm font-semibold text-gray-700 hover:bg-gray-50">
              <Mail className="h-4 w-4" /> {emailSummaryLabel}
            </button>
          )}
          {onEmailGenie && (
            <button type="button" role="menuitem" onClick={() => choose(onEmailGenie)}
              className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-sm font-semibold text-gray-700 hover:bg-gray-50">
              <Mail className="h-4 w-4" /> {emailActionLabel}
            </button>
          )}
          {showHomeScreen && (
            <button
              type="button"
              role="menuitem"
              onClick={() => choose(() => void homeScreen.trigger())}
              className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-[13px] font-semibold text-[#3D4557] transition-colors hover:bg-[#F5F7FB]"
            >
              <SquarePlus size={16} strokeWidth={2} style={{ color: actionButtonStyle?.iconColor || brandColor }} />
              Add to home screen
            </button>
          )}
        </div>
      )}
      {showHomeScreen && homeScreen.mode !== "hidden" && (
        <AddToHomeScreenSheet
          open={homeScreen.instructionsOpen}
          onClose={homeScreen.closeInstructions}
          platform={homeScreen.mode}
          brandColor={brandColor}
          genieName={addToHomeScreen.genieName}
          iconUrl={addToHomeScreen.iconUrl}
        />
      )}
    </div>
  );
};

export default MoreActionsPopover;
